import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import logoAteliePro from "@/assets/logo-atelie-pro.png";
import { LogOut } from "lucide-react";

export default function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // O Supabase envia o hash como âncora na URL (#type=recovery&token=...)
    // Não precisamos verificar, apenas deixar o usuário prosseguir
    console.log("📍 Reset password URL:", location.pathname + location.hash);
  }, [location]);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    if (password !== confirmPassword) {
      toast.error("❌ As senhas não coincidem. Tente novamente.");
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      toast.error("⚠️ A senha deve ter no mínimo 6 caracteres");
      setLoading(false);
      return;
    }

    try {
      const { error } = await supabase.auth.updateUser({
        password: password,
      });

      if (error) throw error;

      // Desconectar de todos os dispositivos após atualizar a senha
      await supabase.auth.signOut({ scope: "others" });

      toast.success("✅ Senha redefinida com sucesso! Você será desconectado de todos os dispositivos. Redirecionando para login...");
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (error: any) {
      // Traduzir mensagens de erro
      let mensagemErro = "Erro ao redefinir senha";

      if (error?.message) {
        const msg = error.message.toLowerCase();
        
        if (msg.includes("invalid")) {
          mensagemErro = "❌ Link inválido ou expirado. Solicite uma nova redefinição de senha.";
        } else if (msg.includes("expired")) {
          mensagemErro = "⏳ Link expirado (válido por 1 hora). Solicite uma nova redefinição.";
        } else if (msg.includes("network") || msg.includes("timeout")) {
          mensagemErro = "🌐 Erro de conexão. Verifique sua internet e tente novamente.";
        } else {
          mensagemErro = error.message || "Erro ao redefinir senha";
        }
      }

      toast.error(mensagemErro);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-800 via-purple-600 to-pink-500 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={logoAteliePro} alt="Ateliê Pro" className="h-16 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-white mb-2">Redefinir Senha</h1>
          <p className="text-purple-100">Digite sua nova senha</p>
        </div>

        <Card className="bg-white/10 backdrop-blur-xl border-white/20 shadow-2xl">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold text-white">Nova Senha</CardTitle>
            <p className="text-purple-100">Escolha uma senha forte</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleResetPassword} className="space-y-4">
              {/* Aviso de segurança - Muito visível */}
              <div className="bg-amber-100 border-2 border-amber-400 rounded-lg p-4 mb-6 shadow-md flex gap-3">
                <LogOut className="h-6 w-6 text-amber-700 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-amber-900 font-bold text-sm leading-tight">
                    ⚠️ Atenção Importante
                  </p>
                  <p className="text-amber-800 text-sm mt-1 leading-relaxed">
                    Ao redefinir sua senha, você será desconectado de <strong>todos os outros dispositivos</strong>. Será necessário fazer login novamente com a nova senha.
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-white">Nova Senha</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-white/20 border-white/30 text-white placeholder:text-purple-200"
                  placeholder="••••••••"
                  required
                  minLength={6}
                />
                <p className="text-xs text-purple-200">Mínimo 6 caracteres</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-white">Confirmar Senha</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="bg-white/20 border-white/30 text-white placeholder:text-purple-200"
                  placeholder="••••••••"
                  required
                  minLength={6}
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-purple-600 via-purple-500 to-pink-500 hover:from-purple-700 hover:via-purple-600 hover:to-pink-600 text-white font-semibold py-3"
                disabled={loading}
              >
                {loading ? "Redefinindo..." : "Redefinir Senha"}
              </Button>
            </form>
            <div className="mt-6 text-center">
              <p className="text-purple-100">
                Lembrou sua senha?{" "}
                <button
                  onClick={() => navigate("/login")}
                  className="text-purple-300 hover:text-purple-200 font-medium underline"
                >
                  Voltar ao Login
                </button>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

